import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectCardData, selectChecklistData } from '../../../../store/card/card.selector';
import { selectUser } from '../../../../store/user/user.selector';
import { fetchCollectionData, fetchChecklistDataByIds, fetchChecklistCardIds } from '../../../../utils/firebase/firebase.utils';
import { getAvailableSeries, getAvailableCardTypes, getAvailableCardTypeCategories } from '../../../../utils/firebase/checklist';
import ChecklistItemDisplay from '../../../../components/checklist-item-display/ChecklistItemDisplay';
import InputDropdown from '../../../../components/input-dropdown/InputDropdown';
import InputBar from '../../../../components/input-bar/InputBar';
import CardAttribute from '../../../../components/card-attribute/CardAttribute';

// styles - using ChecklistItemDisplay styles for PSA card display
import {
  ChecklistItemDisplayContainer,
  CardDetails,
  TopCardContent,
  LeftCardContent,
  CardNumber,
  CardHeader as ChecklistCardHeader,
  AttributesContainer
} from '../../../../components/checklist-item-display/ChecklistItemDisplay.styles';

// styles
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  InputContainer,
  LabelText,
  NavigationContainer,
  Breadcrumb,
  BreadcrumbItem,
  FiltersContainer,
  FilterRow,
  CardsGrid,
  LoadingContainer,
  SelectedCardContainer,
  SelectedCardTitle
} from './NewGradingModal.styles';

export default function NewGradingModal({
  isOpen,
  onClose,
  onChooseCard
}) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const cardData = useSelector(selectCardData);
  const checklistData = useSelector(selectChecklistData);
  
  const [navigationPath, setNavigationPath] = useState(['MLB', '2025', 'Topps']);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [availableCardTypes, setAvailableCardTypes] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState('');
  const [selectedCardType, setSelectedCardType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [gradingLevel, setGradingLevel] = useState('');

  const [sport, year, manufacturer] = navigationPath;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setGradingLevel('');
      setSelectedCard(null);
      setSelectedVariation(null);
      setNavigationPath(['MLB', '2025', 'Topps']);
      setSelectedSeries('');
      setSelectedCardType('');
      setSelectedCategory('');
    }
  }, [isOpen]);

  // Fetch available series
  useEffect(() => {
    const fetchSeries = async () => {
      if (isOpen && user?.uid && sport && year && manufacturer) {
        setIsLoading(true);
        try {
          const seriesData = await getAvailableSeries(user.uid, sport, year, manufacturer);
          const seriesList = Object.keys(seriesData || {}).sort();
          setAvailableSeries(seriesList);
        } catch (error) {
          console.error('Error fetching series:', error);
          setAvailableSeries([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchSeries();
  }, [isOpen, user?.uid, sport, year, manufacturer]);

  // Fetch available card types when series is selected
  useEffect(() => {
    const fetchCardTypes = async () => {
      if (isOpen && user?.uid && sport && year && manufacturer && selectedSeries) {
        setIsLoading(true);
        try {
          const cardTypesData = await getAvailableCardTypes(user.uid, sport, year, manufacturer, selectedSeries);
          const cardTypesList = cardTypesData.map(ct => ct.cardType).sort();
          setAvailableCardTypes(cardTypesList);
        } catch (error) {
          console.error('Error fetching card types:', error);
          setAvailableCardTypes([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchCardTypes();
  }, [isOpen, user?.uid, sport, year, manufacturer, selectedSeries]);

  // Fetch available categories when card type is selected
  useEffect(() => {
    const fetchCategories = async () => {
      if (isOpen && user?.uid && sport && year && manufacturer && selectedSeries && selectedCardType) {
        setIsLoading(true);
        try {
          const categoriesData = await getAvailableCardTypeCategories(user.uid, sport, year, manufacturer, selectedSeries, selectedCardType);
          const categoriesList = Object.keys(categoriesData || {}).sort();
          setAvailableCategories(categoriesList);
        } catch (error) {
          console.error('Error fetching categories:', error);
          setAvailableCategories([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchCategories();
  }, [isOpen, user?.uid, sport, year, manufacturer, selectedSeries, selectedCardType]);

  // Fetch collection data when we have a full path
  useEffect(() => {
    if (isOpen && user?.uid && sport && year && manufacturer && selectedSeries && selectedCardType && selectedCategory) {
      setIsLoading(true);
      const collectionPath = `users/${user.uid}/collection/${sport}/${year}/${manufacturer}/${selectedSeries}/${selectedCardType}/${selectedCategory}`;
      
      Promise.all([
        dispatch(fetchCollectionData(collectionPath)),
        dispatch(fetchChecklistCardIds(sport, year, manufacturer, selectedSeries, selectedCardType, selectedCategory))
      ]).then(([, cardIds]) => {
        if (cardIds && cardIds.length > 0) {
          return dispatch(fetchChecklistDataByIds(sport, year, manufacturer, selectedSeries, selectedCardType, selectedCategory, cardIds));
        }
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [isOpen, user?.uid, sport, year, manufacturer, selectedSeries, selectedCardType, selectedCategory, dispatch]);

  // Get collected cards for display
  const collectedCards = useMemo(() => {
    if (!cardData || Object.keys(cardData).length === 0) return [];
    
    return Object.values(cardData)
      .filter(card => card.collectedCards && card.collectedCards.length > 0)
      .map(card => {
        const checklistCard = checklistData[card.cardNumber];
        const mergedCard = checklistCard
          ? { ...checklistCard, ...card, isCollected: true }
          : { ...card, isCollected: true };
        return mergedCard;
      });
  }, [cardData, checklistData]);

  const handleBreadcrumbClick = (index) => {
    setNavigationPath(prev => prev.slice(0, index + 1));
    if (index < 2) {
      setSelectedSeries('');
      setSelectedCardType('');
      setSelectedCategory('');
    }
  };

  const handleSeriesBreadcrumbClick = () => {
    setSelectedCardType('');
    setSelectedCategory('');
    setSelectedCard(null);
  };

  const handleCardTypeBreadcrumbClick = () => {
    setSelectedCategory('');
    setSelectedCard(null);
  };

  const handleCategoryBreadcrumbClick = () => {
    setSelectedCard(null);
  };

  const handleSeriesChange = (seriesName) => {
    setSelectedSeries(seriesName);
    setSelectedCardType('');
    setSelectedCategory('');
    setSelectedCard(null);
  };

  const handleCardTypeChange = (cardTypeName) => {
    setSelectedCardType(cardTypeName);
    setSelectedCategory('');
    setSelectedCard(null);
  };

  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
    setSelectedCard(null);
  };

  const handleCardSelect = (cardData) => {
    // cardData includes the card and selectedVariation (if a specific variation was selected)
    setSelectedCard(cardData);
    setSelectedVariation(cardData.selectedVariation || null);
  };

  const handleAdd = () => {
    if (selectedCard && gradingLevel.trim() && onChooseCard) {
      onChooseCard({ 
        type: 'graded', 
        grade: gradingLevel,
        card: selectedCard
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setGradingLevel('');
    setSelectedCard(null);
    setSelectedVariation(null);
    setNavigationPath(['MLB', '2025', 'Topps']);
    setSelectedSeries('');
    setSelectedCardType('');
    setSelectedCategory('');
    onClose();
  };

  // Render selected card in PSA format
  const renderSelectedCard = () => {
    if (!selectedCard) return null;

    const card = selectedCard;
    const name = card.name || [];
    const series = card.cardPath?.[3] || '';
    const cardAttributes = card.cardAttributes || [];

    return (
      <SelectedCardContainer>
        <SelectedCardTitle>Selected Card</SelectedCardTitle>
        <ChecklistItemDisplayContainer className='panel-bg dropshadow'>
          <TopCardContent>
            <LeftCardContent>
              <CardNumber>
                <h4>{card.cardNumber}</h4>
              </CardNumber>
              <CardDetails>
                <ChecklistCardHeader>
                  {name.map((playerName, index) => (
                    <span key={index}>
                      <h3>{playerName}</h3>
                      {index < name.length - 1 && ' • '}
                    </span>
                  ))}
                </ChecklistCardHeader>
                {(cardAttributes?.length > 0 || selectedVariation || series) && (
                  <AttributesContainer>
                    {series && (
                      <>
                        <CardAttribute text={series} />
                        {(cardAttributes?.length > 0 || selectedVariation) && <h4>•</h4>}
                      </>
                    )}
                    {cardAttributes?.length > 0 && cardAttributes.map(attribute => (
                      <CardAttribute key={attribute} text={attribute} />
                    ))}
                    {cardAttributes?.length > 0 && selectedVariation && <h4>•</h4>}
                    {selectedVariation && (
                      <CardAttribute
                        color={selectedVariation.cardColor || '#FFFFFF'}
                        text={
                          selectedVariation.cardSupply
                            ? `${selectedVariation.cardParallelName} (${selectedVariation.cardPrintNumber}/${selectedVariation.cardSupply})`
                            : selectedVariation.cardParallelName
                        }
                      />
                    )}
                  </AttributesContainer>
                )}
              </CardDetails>
            </LeftCardContent>
          </TopCardContent>
        </ChecklistItemDisplayContainer>
      </SelectedCardContainer>
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={handleClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='panel-bg' style={{ maxWidth: '1200px' }}>
        <ModalHeader>
          <ModalTitle>Add Graded Card</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Card Selection Section */}
          {!selectedCard && (
            <>
              <NavigationContainer>
                <Breadcrumb>
                  {navigationPath.map((item, index) => (
                    <BreadcrumbItem 
                      key={index}
                      onClick={() => handleBreadcrumbClick(index)}
                      $isActive={index === navigationPath.length - 1 && !selectedSeries}
                    >
                      {item}
                      {' / '}
                    </BreadcrumbItem>
                  ))}
                  {selectedSeries && (
                    <BreadcrumbItem 
                      onClick={handleSeriesBreadcrumbClick}
                      $isActive={!selectedCardType}
                    >
                      {selectedSeries}
                      {selectedCardType && ' / '}
                    </BreadcrumbItem>
                  )}
                  {selectedCardType && (
                    <BreadcrumbItem 
                      onClick={handleCardTypeBreadcrumbClick}
                      $isActive={!selectedCategory}
                    >
                      {selectedCardType}
                      {selectedCategory && ' / '}
                    </BreadcrumbItem>
                  )}
                  {selectedCategory && (
                    <BreadcrumbItem 
                      onClick={handleCategoryBreadcrumbClick}
                      $isActive={true}
                    >
                      {selectedCategory}
                    </BreadcrumbItem>
                  )}
                </Breadcrumb>
              </NavigationContainer>

              <FiltersContainer>
                {selectedSeries === '' && (
                  <FilterRow>
                    <div style={{ flex: 1 }}>
                      <LabelText>Series</LabelText>
                      <InputDropdown
                        id="series-select"
                        value={selectedSeries}
                        onChange={handleSeriesChange}
                        options={availableSeries}
                        placeholder="Select a series"
                      />
                    </div>
                  </FilterRow>
                )}

                {selectedSeries && selectedCardType === '' && (
                  <FilterRow>
                    <div style={{ flex: 1 }}>
                      <LabelText>Card Type</LabelText>
                      <InputDropdown
                        id="card-type-select"
                        value={selectedCardType}
                        onChange={handleCardTypeChange}
                        options={availableCardTypes}
                        placeholder="Select a card type"
                      />
                    </div>
                  </FilterRow>
                )}

                {selectedSeries && selectedCardType && selectedCategory === '' && (
                  <FilterRow>
                    <div style={{ flex: 1 }}>
                      <LabelText>Category</LabelText>
                      <InputDropdown
                        id="category-select"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        options={availableCategories}
                        placeholder="Select a category"
                      />
                    </div>
                  </FilterRow>
                )}
              </FiltersContainer>

              {isLoading ? (
                <LoadingContainer>
                  <p>Loading cards...</p>
                </LoadingContainer>
              ) : selectedSeries && selectedCardType && selectedCategory && collectedCards.length > 0 ? (
                <CardsGrid>
                  {collectedCards.map((card) => (
                    <div 
                      key={card.cardNumber}
                      style={{
                        border: selectedCard?.cardNumber === card.cardNumber 
                          ? '2px solid #4CAF50' 
                          : '1px solid rgba(255,255,255,0.5)',
                        borderRadius: '8px',
                        padding: '4px',
                        transition: 'border-color 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleCardSelect(card)}
                    >
                      <ChecklistItemDisplay
                        card={card}
                        showcaseView={true}
                        onCardSelect={handleCardSelect}
                      />
                    </div>
                  ))}
                </CardsGrid>
              ) : selectedSeries && selectedCardType && selectedCategory && collectedCards.length === 0 ? (
                <LoadingContainer>
                  <p>No cards found in this category.</p>
                </LoadingContainer>
              ) : (
                <LoadingContainer>
                  <p>Select a series, card type, and category to view cards.</p>
                </LoadingContainer>
              )}
            </>
          )}

          {/* Selected Card Display and Grade Input */}
          {selectedCard && (
            <>
              {renderSelectedCard()}
              
              <InputContainer>
                <LabelText>Grade</LabelText>
                <InputBar
                  type="text"
                  value={gradingLevel}
                  updateValue={setGradingLevel}
                  id="grading-level"
                  label="Enter grade (e.g., PSA 10, PSA 9, etc.)"
                  pH="Enter grade"
                />
              </InputContainer>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            {selectedCard ? (
              <>
                <button
                  onClick={handleAdd}
                  disabled={!gradingLevel.trim()}
                  style={{
                    background: gradingLevel.trim() ? 'linear-gradient(135deg, #4CAF50, #45a049)' : 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    cursor: gradingLevel.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '12px 24px',
                    transition: 'all 0.3s ease',
                    minWidth: '120px',
                    opacity: gradingLevel.trim() ? 1 : 0.5
                  }}
                >
                  ADD
                </button>
                <button
                  onClick={() => {
                    setSelectedCard(null);
                    setSelectedVariation(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: '12px 24px',
                    transition: 'all 0.3s ease',
                    minWidth: '120px'
                  }}
                >
                  Change Card
                </button>
              </>
            ) : null}
            <button
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                padding: '12px 24px',
                transition: 'all 0.3s ease',
                minWidth: '120px'
              }}
            >
              Cancel
            </button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
